import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { THEME_COLOR3, THEME_COLOR4 } from '../../../../../constants';

type TSectionAccordionProps = {
  accordionRef?: React.RefObject<HTMLElement> | null;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  iconColor: string;
  headerBackground: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  hasError?: boolean;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
};

const SectionAccordion = ({
  accordionRef,
  title,
  subtitle,
  icon,
  iconColor,
  headerBackground,
  actions,
  children,
  hasError = false,
  expanded,
  onToggle,
}: TSectionAccordionProps) => {
  const [localExpanded, setLocalExpanded] = useState(true);
  const isExpanded = expanded !== undefined ? expanded : localExpanded;

  const handleChange = (_: React.SyntheticEvent, next: boolean) => {
    if (expanded !== undefined) {
      onToggle?.(next);
    } else {
      setLocalExpanded(next);
    }
  };

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Accordion
      ref={accordionRef as any}
      disableGutters
      expanded={isExpanded}
      onChange={handleChange}
      sx={{ ...styles.accordion, ...(hasError ? styles.accordionError : {}) }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={styles.expandIcon} />}
        sx={{ background: headerBackground, ...styles.summary, ...(hasError ? styles.summaryError : {}) }}
      >
        <Box sx={styles.header}>
          <Box sx={{ ...styles.iconBadge, color: iconColor, ...(hasError ? styles.iconBadgeError : {}) }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={styles.title}>{title}</Typography>
            <Typography sx={styles.subtitle}>{subtitle}</Typography>
          </Box>
          {hasError && (
            <Box sx={styles.errorPill}>
              <Typography sx={styles.errorPillText}>Required fields missing</Typography>
            </Box>
          )}
        </Box>
        {actions && (
          <Box sx={styles.actions} onClick={(e) => e.stopPropagation()}>
            {actions}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={styles.details}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default SectionAccordion;

const styles = {
  accordion: {
    mb: 2,
    scrollMarginTop: '72px',
    borderRadius: '12px !important',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FFF',
    boxShadow: 'none',
    overflow: 'hidden',
    '&:before': { display: 'none' },
    transition: 'border-color 0.2s',
  },
  accordionError: {
    borderColor: '#d32f2f',
    borderWidth: '1.5px',
  },
  summary: {
    px: 2.5,
    py: 1.5,
    minHeight: 'unset',
    borderBottom: `1px solid ${THEME_COLOR4}`,
    '& .MuiAccordionSummary-content': { my: 0, alignItems: 'center' },
    transition: 'border-color 0.2s',
  },
  summaryError: {
    borderBottomColor: '#d32f2f',
  },
  header: { display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 },
  iconBadge: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'border-color 0.2s',
  },
  iconBadgeError: {
    borderColor: '#d32f2f',
    backgroundColor: '#fff5f5',
  },
  title: { fontFamily: 'GilroyBold', fontSize: 16, color: '#000' },
  subtitle: { fontFamily: 'GilroyRegular', fontSize: 12, color: THEME_COLOR3 },
  expandIcon: { color: '#000', fontSize: 24 },
  actions: { ml: 'auto', display: 'flex', gap: 1, alignItems: 'center', pr: 1 },
  details: { px: 2.5, py: 2.25 },
  errorPill: {
    ml: 'auto',
    mr: 1,
    px: 1.25,
    py: 0.4,
    borderRadius: '999px',
    backgroundColor: '#fdecea',
    border: '1px solid #f5c6cb',
    flexShrink: 0,
  },
  errorPillText: {
    fontFamily: 'GilroyMedium',
    fontSize: 11,
    color: '#d32f2f',
    whiteSpace: 'nowrap' as const,
  },
};
